import React, { useContext } from 'react';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { Card } from 'components/Card';
import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import When from 'components/When';
import { ICONS, PFE_ICONS } from 'constants/icons';
import { itemPropType } from 'constants/propTypes';
import { useCollapseState } from 'features/shared/useCollapseState';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectLocale } from 'selectors/locale-data';
import { selectActiveVenue } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import { ADD_ITEM_FLYOUT_ID_PREFIX, EDIT_ITEM_FLYOUT_ID_PREFIX, OTHER_CATEGORY_NAME } from '../Items.constants';
import { itemsGroupMessages as messages, itemsMessages } from '../Items.messages';
import { generateFlyoutId } from '../Items.utils';
import { ItemAvailabilityStatusForm } from './ItemAvailabilityStatus';
import { ItemFormContext } from './ItemFormContext';

import './ItemsGroup.scss';

const { block, element } = bem('items-group');

const ItemsGroupHeader = ({ categoryName, handleOpen, isOpen, isMobile }) => {
  const locale = useSelector(selectLocale);
  const intl = useIntl();
  const collapseAriaLabel = intl.formatMessage(messages.collapseCategoryAriaLabel, { categoryName });
  const expandAriaLabel = intl.formatMessage(messages.expandCategoryAriaLabel, { categoryName });

  return (
    <div className={element('header')}>
      <button
        type="button"
        className={element('expand-btn', { hidden: isMobile })}
        onClick={handleOpen}
        aria-expanded={isOpen}
        aria-label={isOpen ? collapseAriaLabel : expandAriaLabel}>
        <SvgArrow direction={isOpen ? 'up' : 'down'} />
      </button>
      <When predicate={categoryName === OTHER_CATEGORY_NAME}>
        <Message as="div" className={element('cell', 'name-cell')} message={messages.otherCategory} />
      </When>
      <When predicate={categoryName !== OTHER_CATEGORY_NAME}>
        <div className={element('cell', 'name-cell')}>{categoryName}</div>
      </When>
      <When predicate={isOpen && !isMobile}>
        <div className={element('cell', 'payout-cell')}>
          <Message as="div" message={messages.payout} />
          <TooltipToggle className={element('tooltip-toggle')} name="items-payout-tooltip">
            <Isvg src={ICONS.infoGreyLight} />
          </TooltipToggle>
          <TooltipContent hoverable name="items-payout-tooltip" place="right">
            <Message
              message={messages.payoutTooltip}
              values={{
                a: text => (
                  <a href={makePath.partnerTaxDefinition(locale)} target="_blank" rel="noopener noreferrer">
                    {text}
                  </a>
                ),
              }}
            />
          </TooltipContent>
        </div>
        <Message as="div" className={element('cell', 'status-cell')} message={messages.availability} />
      </When>
    </div>
  );
};

ItemsGroupHeader.propTypes = {
  categoryName: PropTypes.string,
  handleOpen: PropTypes.func,
  isMobile: PropTypes.bool,
  isOpen: PropTypes.bool,
};

const ItemsGroupRow = ({ isMobile, item, onChangeAvailabilityStatus }) => {
  const intl = useIntl();
  const { setCurrentItem } = useContext(ItemFormContext);
  const { openFlyout } = useContext(FlyoutContext);
  const { name, availabilityStatus, payoutRate } = item;
  const { currencyCode } = useSelector(selectActiveVenue);
  const isPending = payoutRate === null;
  const editItemAriaLabel = intl.formatMessage(messages.editItemAriaLabel, { itemName: name });

  const onChange = onChangeAvailabilityStatus(item);

  const editItemFlyoutId = generateFlyoutId(EDIT_ITEM_FLYOUT_ID_PREFIX, name);
  const handleEditItemFormOpen = () => {
    setCurrentItem(item);
    openFlyout(editItemFlyoutId);
  };

  const duplicateItemFlyoutId = generateFlyoutId(ADD_ITEM_FLYOUT_ID_PREFIX, name);
  const rowDuplicateItem = () => {
    setCurrentItem(item);
    openFlyout(duplicateItemFlyoutId);
  };

  return (
    <div className={element('row')}>
      <button
        className={element('cell', 'row-button')}
        type="button"
        aria-label={editItemAriaLabel}
        onClick={handleEditItemFormOpen}>
        <div className={element('cell', 'name-cell')} title={name}>
          <span className={element('name-text')}>{name}</span>
        </div>
        <When predicate={!isMobile}>
          {payoutRate ? (
            <Message
              as="div"
              className={element('cell', 'payout-cell', 'payout-text')}
              format="currency"
              currency={currencyCode}
              value={payoutRate}
            />
          ) : (
            <div className={element('cell', 'payout-cell', 'payout-text')}>––</div>
          )}
        </When>
      </button>
      <div className={element('cell', 'status-cell', 'status-cell-row')}>
        <span className={element('availability-status')}>
          <ItemAvailabilityStatusForm
            availabilityStatus={availabilityStatus}
            handleEditItemFormOpen={handleEditItemFormOpen}
            isPending={isPending}
            onChange={onChange}
            openDuplicateItem={rowDuplicateItem}
          />
        </span>
        <When predicate={!isMobile}>
          <span className={element('item-settings')}>
            <Dropdown contentClassName={element('settings-dropdown-content')} isSmallRadiusStyle attachment="right">
              {({ toggleMenu }) => (
                <>
                  <button type="button" onClick={toggleMenu}>
                    <Isvg src={PFE_ICONS.threeDots} />
                  </button>
                  <DropdownMenu>
                    <DropdownItem onClick={rowDuplicateItem}>
                      <span className={element('icon-wrapper')}>
                        <Isvg src={ICONS.copy} />
                      </span>
                      <Message message={itemsMessages.duplicateItem} />
                    </DropdownItem>
                    <DropdownItem onClick={handleEditItemFormOpen}>
                      <span className={element('icon-wrapper')}>
                        <Isvg src={ICONS.editPencil} />
                      </span>
                      <Message message={itemsMessages.editItem} />
                    </DropdownItem>
                  </DropdownMenu>
                </>
              )}
            </Dropdown>
          </span>
        </When>
      </div>
    </div>
  );
};

ItemsGroupRow.propTypes = {
  isMobile: PropTypes.bool,
  item: itemPropType,
  onChangeAvailabilityStatus: PropTypes.func,
};

const ItemsGroup = ({ category, items, collapseStorageKey, onAddItem, onChangeAvailabilityStatus }) => {
  const intl = useIntl();
  const addItemAriaLabel = intl.formatMessage(messages.addItemAriaLabel);
  const isMobile = useSelector(selectIsMobile);
  const [isOpen, setIsOpen] = useCollapseState(collapseStorageKey, category, true);

  const toggleIsOpen = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <div className={block()}>
      <Card className={element('container')} collapse flush>
        <ItemsGroupHeader categoryName={category} handleOpen={toggleIsOpen} isMobile={isMobile} isOpen={isOpen} />
        <When predicate={isOpen}>
          <div className={element('body')}>
            {items.map(item => (
              <ItemsGroupRow
                key={item.name}
                isMobile={isMobile}
                item={item}
                onChangeAvailabilityStatus={onChangeAvailabilityStatus}
              />
            ))}
            <div className={element('add-item-row')}>
              <button
                type="button"
                className={element('add-item-btn')}
                aria-label={addItemAriaLabel}
                onClick={onAddItem}>
                <Isvg src={ICONS.addGrey} />
                <Message as="span" message={messages.addItem} />
              </button>
            </div>
          </div>
        </When>
      </Card>
    </div>
  );
};

ItemsGroup.propTypes = {
  category: PropTypes.string,
  items: PropTypes.arrayOf(itemPropType),
  collapseStorageKey: PropTypes.string.isRequired,
  onAddItem: PropTypes.func,
  onChangeAvailabilityStatus: PropTypes.func,
};

export default ItemsGroup;
